/**
 * MapLibre custom protocol handler for cairn://<sourceId>/<b64Template>/<z>/<x>/<y>
 *
 * The real URL template is base64url-encoded into the cairn URL so every tile
 * request carries enough context for the handler to reconstruct the upstream
 * URL without any external state. The handler substitutes {z}/{x}/{y},
 * {bbox-epsg-3857}, and {s} inside the decoded template before fetching
 * through the tile cache (which handles SWR and persistence).
 *
 * Usage:
 *   // At map construction time, once:
 *   maplibregl.addProtocol('cairn', cairnProtocolHandler)
 *
 *   // When adding a raster source:
 *   map.addSource('osm', {
 *     type: 'raster',
 *     tiles: [cairnifyTileUrl('osm', 'https://{s}.tile.osm.org/{z}/{x}/{y}.png')],
 *   })
 */

import type { AddProtocolAction } from 'maplibre-gl'
import { tileCacheService } from './tile-cache-service.js'
import { tileBbox3857 } from '../../lib/tile-math.js'
import { db } from '../../database/schema.js'

const MAX_OVERZOOM_LEVELS = 4

/** Subdomains used when a template contains {s} and no explicit list is provided. */
const DEFAULT_SUBDOMAINS = ['a', 'b', 'c']

/** Per-source round-robin cursor for {s} subdomain selection. */
const subdomainCursor = new Map<string, number>()

/** Encode bytes to URL-safe base64 (no padding). */
function toBase64Url(input: string): string {
  return btoa(input).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** Decode URL-safe base64 back to a string. */
function fromBase64Url(input: string): string {
  const padded = input.replace(/-/g, '+').replace(/_/g, '/')
  const needsPad = padded.length % 4
  const full = needsPad ? padded + '='.repeat(4 - needsPad) : padded
  return atob(full)
}

/**
 * Wrap a raster tile URL template with the cairn:// scheme so it goes through
 * the tile cache. The template may contain {z}/{x}/{y}, {bbox-epsg-3857}, and
 * {s}; all are substituted by the protocol handler, not by MapLibre.
 *
 * @param sourceId - Stable id for the layer (used as the cache key prefix)
 * @param urlTemplate - The real URL template
 */
export function cairnifyTileUrl(sourceId: string, urlTemplate: string): string {
  const b64 = toBase64Url(urlTemplate)
  return `cairn://${encodeURIComponent(sourceId)}/${b64}/{z}/{x}/{y}`
}

/**
 * Wrap a glyph URL template so it goes through the tile cache.
 * MapLibre substitutes {fontstack} and {range} before calling the protocol handler.
 */
export function cairnifyGlyphUrl(glyphTemplate: string): string {
  const b64 = toBase64Url(glyphTemplate)
  return `cairn://glyph/${b64}/{fontstack}/{range}`
}

/**
 * Substitute template placeholders in a tile URL template. Handles
 * {z}/{x}/{y}, {bbox-epsg-3857}, and {s} (with round-robin subdomain
 * selection). Exported so the plan download workflow can build real URLs
 * to hand to `tileCacheService.pinTiles`.
 */
export function substituteTileTemplate(template: string, z: number, x: number, y: number): string {
  return substituteTemplate(template, z, x, y)
}

function substituteTemplate(template: string, z: number, x: number, y: number): string {
  let out = template
    .replace(/\{z\}/g, String(z))
    .replace(/\{x\}/g, String(x))
    .replace(/\{y\}/g, String(y))
  if (out.includes('{bbox-epsg-3857}')) {
    out = out.replace(/\{bbox-epsg-3857\}/g, tileBbox3857(z, x, y))
  }
  if (out.includes('{s}')) {
    out = out.replace(/\{s\}/g, pickSubdomain(out))
  }
  return out
}

function pickSubdomain(templateKey: string): string {
  const cursor = (subdomainCursor.get(templateKey) ?? 0) + 1
  subdomainCursor.set(templateKey, cursor)
  const sub = DEFAULT_SUBDOMAINS[cursor % DEFAULT_SUBDOMAINS.length]
  return sub
}

export interface ColorKeyEntry {
  r: number
  g: number
  b: number
  tolerance: number
}

const colorKeyRegistry = new Map<string, ColorKeyEntry[]>()

export function registerColorKey(sourceIdPrefix: string, keys: ColorKeyEntry[]): void {
  colorKeyRegistry.set(sourceIdPrefix, keys)
}

function findColorKeys(sourceId: string): ColorKeyEntry[] | undefined {
  for (const [prefix, keys] of colorKeyRegistry) {
    if (sourceId.startsWith(prefix)) return keys
  }
  return undefined
}

async function applyColorKey(data: ArrayBuffer, keys: ColorKeyEntry[]): Promise<ArrayBuffer> {
  const blob = new Blob([data], { type: 'image/png' })
  const bitmap = await createImageBitmap(blob, { premultiplyAlpha: 'none' })
  const canvas = new OffscreenCanvas(bitmap.width, bitmap.height)
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(bitmap, 0, 0)
  bitmap.close()

  const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const px = imgData.data
  for (let i = 0; i < px.length; i += 4) {
    for (const key of keys) {
      if (
        Math.abs(px[i] - key.r) <= key.tolerance &&
        Math.abs(px[i + 1] - key.g) <= key.tolerance &&
        Math.abs(px[i + 2] - key.b) <= key.tolerance
      ) {
        px[i + 3] = 0
        break
      }
    }
  }
  ctx.putImageData(imgData, 0, 0)
  const outBlob = await canvas.convertToBlob({ type: 'image/png' })
  return outBlob.arrayBuffer()
}

const CAIRN_URL_RE = /^cairn:\/\/([^/]+)\/([A-Za-z0-9_-]+)\/(\d+)\/(\d+)\/(\d+)(?:\?.*)?$/
const CAIRN_GLYPH_RE = /^cairn:\/\/glyph\/([A-Za-z0-9_-]+)\/(.+)\/(\d+-\d+)(?:\?.*)?$/

function isVectorContentType(contentType: string): boolean {
  return (
    contentType.includes('protobuf') ||
    contentType.includes('mapbox-vector-tile') ||
    contentType.includes('x-mvt')
  )
}

async function findCachedAncestor(
  sourceId: string,
  z: number,
  x: number,
  y: number
): Promise<{
  blob: Blob
  contentType: string
  dz: number
  ancestorX: number
  ancestorY: number
} | null> {
  let cx = x
  let cy = y
  for (let dz = 1; dz <= MAX_OVERZOOM_LEVELS && z - dz >= 0; dz++) {
    cx = Math.floor(cx / 2)
    cy = Math.floor(cy / 2)
    const parentKey = `${sourceId}/${z - dz}/${cx}/${cy}`
    const cached = await db.tileCache.get(parentKey)
    if (cached) {
      return {
        blob: cached.blob,
        contentType: cached.contentType,
        dz,
        ancestorX: cx,
        ancestorY: cy,
      }
    }
  }
  return null
}

async function cropRasterTile(
  parentBlob: Blob,
  x: number,
  y: number,
  dz: number
): Promise<ArrayBuffer> {
  const parentBitmap = await createImageBitmap(parentBlob, { premultiplyAlpha: 'none' })
  const size = parentBitmap.width
  const scale = 1 << dz
  const regionSize = size / scale
  const offsetX = (x % scale) * regionSize
  const offsetY = (y % scale) * regionSize

  const bitmap = await createImageBitmap(parentBlob, offsetX, offsetY, regionSize, regionSize, {
    resizeWidth: size,
    resizeHeight: size,
    resizeQuality: 'low',
    premultiplyAlpha: 'none',
  })
  parentBitmap.close()

  const canvas = new OffscreenCanvas(size, size)
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(bitmap, 0, 0)
  bitmap.close()

  const outputBlob = await canvas.convertToBlob({ type: 'image/png' })
  return outputBlob.arrayBuffer()
}

/**
 * MapLibre protocol handler for the `cairn://` scheme.
 *
 * Handles two URL formats:
 *   cairn://<sourceId>/<b64Template>/<z>/<x>/<y>  — tile requests
 *   cairn://glyph/<b64Template>/<fontstack>/<range> — glyph requests
 */
export const cairnProtocolHandler: AddProtocolAction = async (params, abortController) => {
  if (abortController?.signal.aborted) {
    throw new Error('aborted')
  }

  const glyphMatch = CAIRN_GLYPH_RE.exec(params.url)
  if (glyphMatch) {
    return handleGlyph(glyphMatch)
  }

  const match = CAIRN_URL_RE.exec(params.url)
  if (!match) {
    throw new Error(`invalid cairn url: ${params.url}`)
  }

  return handleTile(match, abortController)
}

async function handleGlyph(match: RegExpExecArray): Promise<{ data: ArrayBuffer }> {
  const template = fromBase64Url(match[1])
  const fontstack = decodeURIComponent(match[2])
  const range = match[3]
  const realUrl = template.replace('{fontstack}', fontstack).replace('{range}', range)
  const cacheKey = `glyph/${fontstack}/${range}`

  const blob = await tileCacheService.getResource(cacheKey, realUrl)
  if (blob) return { data: await blob.arrayBuffer() }

  throw new Error(`glyph unavailable: ${fontstack}/${range}`)
}

async function handleTile(
  match: RegExpExecArray,
  abortController: AbortController | undefined
): Promise<{ data: ArrayBuffer }> {
  const sourceId = decodeURIComponent(match[1])
  const template = fromBase64Url(match[2])
  const z = parseInt(match[3], 10)
  const x = parseInt(match[4], 10)
  const y = parseInt(match[5], 10)
  const realUrl = substituteTemplate(template, z, x, y)

  if (abortController?.signal.aborted) {
    throw new Error('aborted')
  }

  const colorKeys = findColorKeys(sourceId)

  const blob = await tileCacheService.getTile(sourceId, realUrl, z, x, y)
  if (blob) {
    let data = await blob.arrayBuffer()
    if (colorKeys) data = await applyColorKey(data, colorKeys)
    return { data }
  }

  // Overzoom fallback: walk up parent tiles for graceful degradation
  const ancestor = await findCachedAncestor(sourceId, z, x, y)
  if (ancestor) {
    if (isVectorContentType(ancestor.contentType)) {
      return { data: await ancestor.blob.arrayBuffer() }
    }
    let data = await cropRasterTile(ancestor.blob, x, y, ancestor.dz)
    if (colorKeys) data = await applyColorKey(data, colorKeys)
    return { data }
  }

  throw new Error(`tile unavailable: ${sourceId}/${z}/${x}/${y}`)
}
